import type { TeamParticipantDto } from "@bafe/api-client";
import { Button, Card, EmptyState, PageHeader, StatusPill, useToast } from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ErrorPanel } from "../../components/ErrorPanel";
import { Popup } from "../../components/Popup";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { fieldError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";
import { useSession } from "../../lib/session";
import { activeMembership } from "../../lib/session";
import { useOrganisationStore } from "../../lib/stores";

export function ManageTeamParticipantsPage() {
	const session = useSession();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const membership = session.data ? activeMembership(session.data, organisationId) : null;
	const canManage = membership?.role === "owner" || membership?.role === "admin";
	const [inviteOpen, setInviteOpen] = useState(false);

	const participants = useQuery({
		queryKey: queryKeys.teamParticipants,
		queryFn: () => api.listTeamParticipants(),
	});

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Team"
				description="People who can join Team conversations in this workspace. This does not grant access to Mail, Hub, or other apps."
				action={
					canManage ? (
						<Button variant="primary" onClick={() => setInviteOpen(true)}>
							Invite to Team
						</Button>
					) : null
				}
			/>

			<QueryState query={participants} errorTitle="We could not load Team participants.">
				{(list) => (
					<ParticipantList
						participants={list}
						canManage={canManage}
						onInvite={() => setInviteOpen(true)}
					/>
				)}
			</QueryState>

			<InviteTeamParticipant open={inviteOpen} onClose={() => setInviteOpen(false)} />
		</div>
	);
}

function ParticipantList({
	participants,
	canManage,
	onInvite,
}: {
	participants: TeamParticipantDto[];
	canManage: boolean;
	onInvite: () => void;
}) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [removing, setRemoving] = useState<TeamParticipantDto | null>(null);

	const remove = useMutation({
		mutationFn: (row: TeamParticipantDto) => api.removeTeamParticipant(row.id),
		onSuccess: (_result, row) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.teamParticipants });
			setRemoving(null);
			toast({
				tone: "success",
				message:
					row.status === "invited"
						? "That invitation was cancelled."
						: "They no longer have Team access in this workspace.",
			});
		},
	});

	if (participants.length === 0) {
		return (
			<Card>
				<EmptyState
					title="No Team participants yet"
					description="Invite colleagues from other workspaces or add people who only need Team conversations."
					action={
						canManage ? (
							<Button variant="primary" onClick={onInvite}>
								Invite to Team
							</Button>
						) : undefined
					}
				/>
			</Card>
		);
	}

	return (
		<>
			{remove.isError ? <ErrorPanel error={remove.error} title="We could not remove that participant." /> : null}
			<Card flush>
				<ul>
					{participants.map((row) => {
						const displayName = row.fullName || row.email;
						return (
							<li
								key={row.id}
								className="flex flex-col gap-2 border-b border-(--border-subtle) px-4 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
							>
								<div className="flex min-w-0 flex-1 flex-col gap-0.5">
									<div className="flex flex-wrap items-center gap-2">
										<p className="text-sm font-medium text-(--text-primary)">{displayName}</p>
										{row.status === "invited" ? <StatusPill tone="pending">Invited</StatusPill> : null}
										{row.teamOnly ? <StatusPill tone="info">Team only</StatusPill> : null}
									</div>
									{row.fullName ? (
										<p className="text-sm text-(--text-secondary)">{row.email}</p>
									) : null}
									<p className="text-xs text-(--text-muted)">Source workspace: {row.sourceWorkspace}</p>
								</div>
								{canManage ? (
									<Button variant="ghost" size="sm" onClick={() => setRemoving(row)}>
										{row.status === "invited" ? "Cancel invitation" : "Remove"}
									</Button>
								) : null}
							</li>
						);
					})}
				</ul>
			</Card>

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title={
					removing
						? removing.status === "invited"
							? `Cancel the invitation for ${removing.email}?`
							: `Remove ${removing.fullName || removing.email} from Team?`
						: "Remove participant"
				}
				description={
					removing?.status === "invited"
						? "The invitation link will stop working."
						: "They lose Team access in this workspace. Other workspace apps and conversations elsewhere are not affected."
				}
				confirmLabel={removing?.status === "invited" ? "Cancel invitation" : "Remove from Team"}
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing);
				}}
			/>
		</>
	);
}

function InviteTeamParticipant({ open, onClose }: { open: boolean; onClose: () => void }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [email, setEmail] = useState("");

	const invite = useMutation({
		mutationFn: () => api.inviteTeamParticipant(email.trim()),
		onSuccess: (result) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.teamParticipants });
			setEmail("");
			onClose();
			toast({
				tone: "success",
				message:
					result.status === "invited"
						? `We sent a Team invitation to ${result.email}.`
						: `${result.email} can use Team in this workspace now.`,
			});
		},
	});

	function close() {
		if (invite.isPending) return;
		onClose();
	}

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (email.trim().length === 0) return;
		invite.mutate();
	}

	return (
		<Popup open={open} title="Invite to Team" onClose={close}>
			<form onSubmit={submit} noValidate>
				<p className="sub">
					They can join Team conversations here. This does not add them to workspace Access or other apps.
				</p>
				{invite.isError ? (
					<p className="err">{fieldError(invite.error, "email") ?? "We could not send that invitation."}</p>
				) : null}
				<label htmlFor="team-participant-email">Email address</label>
				<input
					id="team-participant-email"
					type="email"
					value={email}
					required
					autoComplete="off"
					autoCapitalize="none"
					spellCheck={false}
					autoFocus
					onChange={(event) => setEmail(event.target.value)}
				/>
				<p className="actions">
					<button className="btn" type="submit" disabled={invite.isPending}>
						{invite.isPending ? "Sending" : "Send invitation"}
					</button>
					<button className="btn ghost" type="button" onClick={close}>
						Cancel
					</button>
				</p>
			</form>
		</Popup>
	);
}
